import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import * as R from 'ramda'
import { StoryPeerType } from '@weblite/ts-action/story'
import { IconButton, Skeleton, styled, Typography } from '@mui/material'
import { DeleteIcon, VisibilityIcon } from '@weblite/icons'
import { BottomDrawer, PULLER_HEIGHT } from '$common/bottom-drawer'
import { UserListItem, UserListItemSkeleton } from '$common/user-list-item'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { useGetViewersList } from '$logic/story/react-hooks'
import { usePrevious } from '$common/react-hooks/use-previous'
import {
  VirtualizedInfiniteList,
  VirtualizedListOnScroll,
} from '$common/virtualized-list'
import { useWindowHeight } from '$func/window.react-hook'
import { CHAT_LIST_APP_BAR_HEIGHT } from '$common/app-bar/app-bar.constants'
import { useIsDesktopSize } from '$common/react-hooks/layout'

const LIST_ITEM_SIZE = 56
const TOP_BAR_HEIGHT = 56
const DEFAULT_LIST_HEIGHT = 5 * LIST_ITEM_SIZE
const DESKTOP_LIST_HEIGHT = 350
const LIST_PADDING = 4

export const ViewersList = memo(ViewersListComponent)

export function ViewersListComponent({
  peerId,
  storyId,
  peerType,
  onClose,
  isOpen,
  onDelete,
  viewCount,
}: {
  peerId: string
  storyId?: string
  peerType: StoryPeerType
  onClose: () => void
  isOpen: boolean
  onDelete: () => unknown
  viewCount: number | undefined
}) {
  const { views, isLoading, loadMore, reset, isDone } = useGetViewersList(
    peerId,
    peerType,
    storyId,
  )
  const prevStoryId = usePrevious(storyId)
  const isDesktop = useIsDesktopSize()
  const [height, setHeight] = useState(DEFAULT_LIST_HEIGHT)

  const resetHeight = useCallback(() => {
    setHeight(isDesktop ? DESKTOP_LIST_HEIGHT : DEFAULT_LIST_HEIGHT)
  }, [isDesktop])

  const windowHeight = useWindowHeight()

  useEffect(() => {
    if (storyId !== prevStoryId) reset()
    if (isOpen) loadMore()
  }, [isOpen, storyId, prevStoryId])

  useLayoutEffect(() => {
    if (isOpen) resetHeight()
  }, [isOpen, resetHeight])

  const handleScroll: VirtualizedListOnScroll = useCallback(
    props => {
      const maxHeight =
        windowHeight - TOP_BAR_HEIGHT - PULLER_HEIGHT - CHAT_LIST_APP_BAR_HEIGHT

      // if (!props.scrollOffset) {
      //   setHeight(Math.min(DEFAULT_HEIGHT, maxHeight))
      //   return
      // }

      if (!props.scrollOffset) return

      if (props.scrollDirection === 'forward')
        setHeight(
          R.clamp(
            DEFAULT_LIST_HEIGHT,
            maxHeight,
            views.length * LIST_ITEM_SIZE + LIST_PADDING * 2,
          ),
        )
    },
    [windowHeight, views],
  )

  return (
    <BottomDrawer onClose={onClose} isOpen={isOpen}>
      <TopBar viewCount={viewCount} onDelete={onDelete} isLoading={isLoading} />
      <ListWrapper sx={{ height }}>
        {viewCount || isLoading ? (
          views.length ? (
            <>
              <VirtualizedInfiniteList
                listPadding={LIST_PADDING}
                onScroll={isDesktop ? undefined : handleScroll}
                hasMoreItemToLoad={!isDone}
                loadMore={loadMore}
                height={height}
                items={views}
                ListItem={view => (
                  <UserListItem userId={view.viewerId} disableAvatarClick />
                )}
                SkeletonItem={() => <UserListItemSkeleton />}
                itemSize={LIST_ITEM_SIZE}
              />
            </>
          ) : isLoading ? (
            <>
              {Array.from(Array(5)).map(() => (
                <UserListItemSkeleton />
              ))}
            </>
          ) : null
        ) : (
          <Typography margin="auto" variant="body2" color="neutral600">
            {t('story.noViewedYet')}
          </Typography>
        )}
      </ListWrapper>
    </BottomDrawer>
  )
}

const ListWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 0 1px;
  transition: height 0.6s ease;
`

function TopBar({
  viewCount,
  onDelete,
  isLoading,
}: {
  onDelete: () => unknown
  viewCount: number | undefined
  isLoading: boolean
}) {
  return (
    <Root>
      <FlexRow sx={{ gap: '8px' }}>
        <VisibilityIcon />
        {isLoading ? (
          <Skeleton variant="text" width="10px" />
        ) : (
          <Typography variant="subtitle1">
            {toLocale(viewCount || 0)}
          </Typography>
        )}
      </FlexRow>
      <IconButton onClick={onDelete}>
        <DeleteIcon sx={{ color: 'error.main' }} />
      </IconButton>
    </Root>
  )
}
const FlexRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Root = styled(FlexRow)`
  height: ${TOP_BAR_HEIGHT}px;
  width: 100%;
  padding: 8px 16px 8px 8px;
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.palette.neutral200};
`
